// SPDX-License-Identifier: Elastic-2.0
/**
 * VoiceSettingsSection — Settings card for the voice assistant (F-225, task 7).
 *
 * Lets the user pick their wake phrase, preferred voice, and language.
 * Mirrors `LanguageSection`'s card/token idiom: a `<Card as="section">`
 * with token-only styling and `data-testid`s for tests.
 *
 * Tiering (ADR-002, updated 2026-06): voice control is a **paid feature**.
 * Free (and local-only) callers see an upgrade prompt — the settings query
 * is not fired for them, so they never hit the gateway's 402. Plus/Pro get
 * the full form (with cloud-quality voices).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Card } from "@/components/Card/Card";
import { Button } from "@/components/Button";
import { TierBadge } from "@/components/TierBadge/TierBadge";
import { useAuth } from "@/api/auth-store";
import { ROUTES } from "@/routing/routes";

import { useSaveVoiceSettings, useVoiceSettings } from "./useVoiceSettings";
import type { VoiceSettings } from "./api";
import styles from "./VoiceSettingsSection.module.css";

// Porcupine ships a fixed set of pre-trained wake words (F-225 out of
// scope: custom training). These are the ones Mirlai exposes.
const WAKE_PHRASES = ["Hey Mirlai", "Computer", "Jarvis", "Hey Google"] as const;

// Voice is Plus/Pro-only, so every entitled caller can use cloud voices.
const VOICES = [
  { id: "cloud-en-sophia", label: "Sophia" },
  { id: "cloud-en-james", label: "James" },
  { id: "piper-en-amy", label: "Amy (on-device)" },
  { id: "piper-en-ryan", label: "Ryan (on-device)" },
];

const LANGUAGES = [{ id: "en", label: "English" }];

const FALLBACK: VoiceSettings = {
  wake_phrase: "Hey Mirlai",
  voice: "cloud-en-sophia",
  language: "en",
};

export function VoiceSettingsSection(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tier = useAuth((s) => s.tier);
  const isPaid = tier === "plus" || tier === "pro";

  // Only fetch (and thus only allow saving) for entitled tiers — Free
  // would get a 402 from the gateway.
  const { data } = useVoiceSettings({ enabled: isPaid });
  const save = useSaveVoiceSettings();

  const [draft, setDraft] = useState<VoiceSettings>(FALLBACK);

  // Sync the draft from the server whenever fresh data lands (and on
  // first load). Local edits between fetches are preserved by keying the
  // effect on the server payload identity only.
  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  function update<K extends keyof VoiceSettings>(
    key: K,
    value: VoiceSettings[K],
  ): void {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave(): void {
    save.mutate(draft);
  }

  if (!isPaid) {
    return (
      <Card as="section" variant="elevated" padding="md" data-testid="settings-voice">
        <div className={styles.paywallHeader}>
          <h2 className={styles.title}>{t("voice.settings.section_title")}</h2>
          <TierBadge kind="plus" />
        </div>
        <p className={styles.paywallTitle}>{t("voice.settings.paywall_title")}</p>
        <p className={styles.hint}>{t("voice.settings.paywall_body")}</p>
        <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.PLAN)}
            data-testid="settings-voice-upgrade"
          >
            {t("voice.settings.paywall_cta")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card as="section" variant="elevated" padding="md" data-testid="settings-voice">
      <h2 className={styles.title}>{t("voice.settings.section_title")}</h2>

      <label className={styles.field}>
        <span className={styles.label}>
          {t("voice.settings.wake_phrase_label")}
        </span>
        <select
          className={styles.select}
          value={draft.wake_phrase}
          onChange={(e) => update("wake_phrase", e.target.value)}
          data-testid="settings-voice-wake-phrase"
        >
          {WAKE_PHRASES.map((phrase) => (
            <option key={phrase} value={phrase}>
              {phrase}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span className={styles.label}>{t("voice.settings.voice_label")}</span>
        <select
          className={styles.select}
          value={draft.voice}
          onChange={(e) => update("voice", e.target.value)}
          data-testid="settings-voice-voice"
        >
          {VOICES.map((v) => (
            <option key={v.id} value={v.id}>
              {v.label}
            </option>
          ))}
        </select>
      </label>

      <label className={styles.field}>
        <span className={styles.label}>
          {t("voice.settings.language_label")}
        </span>
        <select
          className={styles.select}
          value={draft.language}
          onChange={(e) => update("language", e.target.value)}
          data-testid="settings-voice-language"
        >
          {LANGUAGES.map((l) => (
            <option key={l.id} value={l.id}>
              {l.label}
            </option>
          ))}
        </select>
      </label>

      <div className={styles.actions}>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={save.isPending}
          data-testid="settings-voice-save"
        >
          {t("voice.settings.save")}
        </Button>
        {save.isSuccess && (
          <span className={styles.savedNote} data-testid="settings-voice-saved">
            {t("voice.settings.saved")}
          </span>
        )}
        {save.isError && (
          <span className={styles.errorNote} data-testid="settings-voice-error">
            {t("voice.settings.save_error")}
          </span>
        )}
      </div>
    </Card>
  );
}

export default VoiceSettingsSection;
